<div class="order-detail-page">
  <app-spinner [loading]="loading">
    @if (notFound) {
      <div class="detail-container">
        <p class="detail-value">{{ 'orders.detail.notFound' | transloco }}</p>
      </div>
    } @else if (loadError) {
      <div class="detail-container detail-error" role="alert">
        <p class="detail-value">{{ 'orders.detail.loadError' | transloco }}</p>
        <app-button
          [label]="'common.actions.retry' | transloco"
          variant="secondary"
          size="small"
          (onClick)="loadOrder()"
        ></app-button>
      </div>
    } @else if (order) {
      <div class="detail-container">
        <!-- Header -->
        <div class="detail-header">
          <div class="header-title">
            <h3 class="order-title">{{ order.amount }} {{ order.product?.name || ('orders.fallback.product' | transloco) }}</h3>
            <p class="order-id">{{ 'orders.detail.orderNumber' | transloco: { id: order.id } }} · {{ order.platform?.name || '-' }}</p>
          </div>
          <div class="header-spacer"></div>
          <div class="header-tags">
            @if (paymentTag) {
              <span
                class="header-tag"
                [attr.title]="('orders.detail.tags.payment' | transloco) + ': ' + paymentTag.label"
              >
                <span class="header-tag-caption">{{ 'orders.detail.tags.payment' | transloco }}</span>
                <app-tag [label]="paymentTag.label" [color]="paymentTag.color" size="small"></app-tag>
              </span>
            }
            @if (providerTag) {
              <span
                class="header-tag"
                [attr.title]="('orders.detail.tags.provider' | transloco) + ': ' + providerTag.label"
              >
                <span class="header-tag-caption">{{ 'orders.detail.tags.provider' | transloco }}</span>
                <app-tag [label]="providerTag.label" [color]="providerTag.color" size="small"></app-tag>
              </span>
            }
          </div>
        </div>

        <!-- Order -->
        <section class="detail-section">
          <h4 class="section-title">{{ 'orders.detail.sections.order' | transloco }}</h4>
          <div class="detail-grid">
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.service' | transloco }}</span>
              <span class="detail-value">{{ order.product?.name || '-' }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.platform' | transloco }}</span>
              <span class="detail-value">{{ order.platform?.name || '-' }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.quantity' | transloco }}</span>
              <span class="detail-value">{{ order.amount }}</span>
            </div>
            <div class="detail-field detail-field--wide">
              <span class="detail-label">{{ 'orders.detail.fields.link' | transloco }}</span>
              <a class="detail-value detail-link" [href]="order.url" target="_blank" rel="noopener noreferrer">{{ order.url }}</a>
            </div>
            @if (order.customComments) {
              <div class="detail-field detail-field--wide">
                <span class="detail-label">{{ 'orders.detail.fields.customComments' | transloco }}</span>
                <pre class="detail-value detail-pre">{{ order.customComments }}</pre>
              </div>
            }
            @if (order.subscription) {
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.subscription' | transloco }}</span>
                <span class="detail-value">
                  #{{ order.subscription.id }} · {{ 'orders.detail.subscriptionRuns' | transloco: { completed: order.subscription.completedRuns, total: order.subscription.totalRuns } }}
                </span>
              </div>
            }
          </div>
        </section>

        <!-- Client -->
        <section class="detail-section">
          <h4 class="section-title">{{ 'orders.detail.sections.client' | transloco }}</h4>
          <div class="detail-grid">
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.name' | transloco }}</span>
              <span class="detail-value">{{ userName }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.email' | transloco }}</span>
              <span class="detail-value">{{ order.user?.email || '-' }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.phone' | transloco }}</span>
              <span class="detail-value">{{ order.user?.phone || '-' }}</span>
            </div>
          </div>
        </section>

        <!-- Payment -->
        <section class="detail-section">
          <h4 class="section-title">{{ 'orders.detail.sections.payment' | transloco }}</h4>
          <div class="detail-grid">
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.price' | transloco }}</span>
              <span class="detail-value detail-value--strong">{{ money(order.price) }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.paymentMethod' | transloco }}</span>
              <span class="detail-value">{{ paymentMethodLabel }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.paymentStatus' | transloco }}</span>
              <span class="detail-value">{{ paymentTag?.label || '-' }}</span>
            </div>
            @if (hasDiscount) {
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.originalPrice' | transloco }}</span>
                <span class="detail-value">{{ money(order.originalPrice) }}</span>
              </div>
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.discount' | transloco }}</span>
                <span class="detail-value">
                  -{{ money(order.discountAmount) }}
                  @if (order.discount) {
                    · {{ order.discount.code || order.discount.name }}
                  }
                </span>
              </div>
            }
            @if (isAdmin && order.stripePaymentIntentId) {
              <div class="detail-field detail-field--wide">
                <span class="detail-label">{{ 'orders.detail.fields.stripePaymentIntent' | transloco }}</span>
                <span class="detail-value detail-mono">{{ order.stripePaymentIntentId }}</span>
              </div>
            }
            @if (isAdmin && order.stripeCheckoutSessionId) {
              <div class="detail-field detail-field--wide">
                <span class="detail-label">{{ 'orders.detail.fields.stripeCheckoutSession' | transloco }}</span>
                <span class="detail-value detail-mono">{{ order.stripeCheckoutSessionId }}</span>
              </div>
            }
          </div>

          @if (isCrypto) {
            <div class="detail-grid detail-grid--sub">
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.cryptoCurrency' | transloco }}</span>
                <span class="detail-value">{{ order.cryptoCurrency }}</span>
              </div>
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.cryptoAmount' | transloco }}</span>
                <span class="detail-value">{{ order.cryptoAmount ?? '-' }}</span>
              </div>
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.exchangeRate' | transloco }}</span>
                <span class="detail-value">{{ order.cryptoExchangeRate ?? '-' }}</span>
              </div>
              <div class="detail-field detail-field--wide">
                <span class="detail-label">{{ 'orders.detail.fields.wallet' | transloco }}</span>
                <span class="detail-value detail-mono">{{ order.cryptoWalletAddress || '-' }}</span>
              </div>
              <div class="detail-field detail-field--wide">
                <span class="detail-label">{{ 'orders.detail.fields.txHash' | transloco }}</span>
                <span class="detail-value detail-mono">{{ order.cryptoTxHash || '-' }}</span>
              </div>
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.expires' | transloco }}</span>
                <span class="detail-value">{{ dateTime(order.cryptoExpiresAt) }}</span>
              </div>
            </div>
          }
        </section>

        <!-- Provider (admin only: the backend does not send it to clients) -->
        @if (order.provider) {
          <section class="detail-section">
            <h4 class="section-title">{{ 'orders.detail.sections.provider' | transloco }}</h4>
            <div class="detail-grid">
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.provider' | transloco }}</span>
                <span class="detail-value">{{ order.provider.name }}</span>
              </div>
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.providerStatus' | transloco }}</span>
                <span class="detail-value">{{ providerTag?.label || '-' }}</span>
              </div>
              <div class="detail-field">
                <span class="detail-label">{{ 'orders.detail.fields.providerOrderId' | transloco }}</span>
                <span class="detail-value detail-mono">{{ order.externalOrderId || '-' }}</span>
              </div>
            </div>
          </section>
        }

        <!-- Dates -->
        <section class="detail-section">
          <h4 class="section-title">{{ 'orders.detail.sections.dates' | transloco }}</h4>
          <div class="detail-grid">
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.created' | transloco }}</span>
              <span class="detail-value">{{ dateTime(order.createdAt) }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.updated' | transloco }}</span>
              <span class="detail-value">{{ dateTime(order.updatedAt) }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">{{ 'orders.detail.fields.completed' | transloco }}</span>
              <span class="detail-value">{{ dateTime(order.completedAt) }}</span>
            </div>
          </div>
        </section>
      </div>
    }
  </app-spinner>
</div>
